/**
 * webgazerController.js
 *
 * Thin wrapper around Brown University's WebGazer.js (served from
 * /public/webgazer/) that drives the questionnaire eye-tracking flow:
 *
 *   1. load()        — inject the library <script> once
 *   2. start()       — begin tracking on the webcam, install a single gaze
 *                      listener that forwards predictions to the app AND (when
 *                      requested) buffers them for the accuracy test
 *   3. calibrate()   — non-click, point-based calibration (2 laps × 9 points)
 *                      driven entirely by recordScreenPosition() on a timeline,
 *                      so no mouse involvement can contaminate the model
 *   4. test()        — multi-point random accuracy test → mean error in pixels
 *   5. stop()        — tear WebGazer down
 *
 * The calibration/test dots are rendered imperatively into a caller-provided
 * container (fixed, full-viewport) so the intricate rAF sampling timeline is
 * reused verbatim from the validated test harness rather than reimplemented in
 * React. Gaze predictions are viewport-pixel coordinates; the recording layer
 * converts them to viewport percentages to match the mouse-event schema.
 *
 * Everything runs client-side. No video or gaze data leaves the browser from
 * here — persistence is the app's responsibility.
 */

const WEBGAZER_SCRIPT_SRC = '/webgazer/webgazer.js';
const FACE_MESH_SOLUTION_PATH = '/webgazer/mediapipe/face_mesh';

// Calibration timing (ported from the validated test harness).
const DWELL_MS = 5000; // time each dot is shown
const SAMPLE_START_MS = 1000; // ignore the first second (eye still landing)
const SAMPLE_END_MS = 4500; // stop before the dot moves (pre-saccade guard)
const SAMPLES_PER_POINT = 5; // samples captured per point per lap
const LAPS = 2; // full passes over the 9 points

// 9 calibration points at viewport edges/centre; centre (index 4) revealed last.
// Top row sits at the very top (y:2) for full top-of-page coverage. The browser's
// fullscreen "press esc to exit" banner may briefly cover the top-centre dot on
// the first pass but auto-hides after a few seconds.
const CALIBRATION_POSITIONS = [
  { x: 2, y: 2 }, { x: 50, y: 2 }, { x: 98, y: 2 },
  { x: 2, y: 50 }, { x: 50, y: 50 }, { x: 98, y: 50 },
  { x: 2, y: 98 }, { x: 50, y: 98 }, { x: 98, y: 98 },
];
const CALIBRATION_ORDER = [0, 1, 2, 3, 5, 6, 7, 8, 4];

// Accuracy test.
const TEST_NUM_POINTS = 5;
const TEST_SETTLE_MS = 1200; // move gaze onto the dot (samples discarded)
const TEST_COLLECT_MS = 1500; // collect scoring samples

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

let scriptPromise = null;

/** Inject the WebGazer <script> exactly once and resolve when window.webgazer exists. */
function loadWebgazerScript() {
  if (window.webgazer) return Promise.resolve(window.webgazer);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${WEBGAZER_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.webgazer));
      existing.addEventListener('error', () => reject(new Error('Failed to load WebGazer')));
      if (window.webgazer) resolve(window.webgazer);
      return;
    }
    const script = document.createElement('script');
    script.src = WEBGAZER_SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      if (window.webgazer) resolve(window.webgazer);
      else reject(new Error('WebGazer loaded but global is missing'));
    };
    script.onerror = () => reject(new Error('Failed to load WebGazer script'));
    document.body.appendChild(script);
  });
  return scriptPromise;
}

class WebgazerController {
  constructor() {
    this.wg = null;
    this.running = false;
    this.onGaze = null; // external per-frame callback {x, y} in viewport px
    this._collecting = false; // accuracy-test sampling flag
    this._buffer = [];
    this._debugDot = null; // optional live troubleshooting dot
  }

  isRunning() {
    return this.running;
  }

  /**
   * Load + begin WebGazer. Returns once the library is initialised.
   * @param {(p:{x:number,y:number})=>void} [onGaze] per-frame gaze callback
   */
  async start(onGaze = null) {
    const webgazer = await loadWebgazerScript();
    this.wg = webgazer;
    this.onGaze = onGaze;

    webgazer.params.faceMeshSolutionPath = FACE_MESH_SOLUTION_PATH;
    // Hide all WebGazer UI BEFORE begin() so the webcam feed never flashes on
    // screen — begin() creates these elements already hidden rather than showing
    // them and letting us hide them a frame later.
    webgazer.params.showVideo = false;
    webgazer.params.showVideoPreview = false;
    webgazer.params.showFaceOverlay = false;
    webgazer.params.showFaceFeedbackBox = false;
    webgazer.params.showGazeDot = false;

    // Single internal listener → forwards to the app and, when scoring, buffers.
    webgazer.setGazeListener((data) => {
      if (data == null) return;
      if (this.onGaze) this.onGaze({ x: data.x, y: data.y });
      if (this._collecting) this._buffer.push({ x: data.x, y: data.y });
      if (this._debugDot) {
        // Draw at the exact prediction we store, so what you see == what's captured.
        this._debugDot.style.left = data.x + 'px';
        this._debugDot.style.top = data.y + 'px';
      }
    });

    await webgazer.begin();

    webgazer
      .showVideoPreview(false)
      .showPredictionPoints(false)
      .applyKalmanFilter(true)
      .setRegression('ridge')
      .saveDataAcrossSessions(false);

    // Keep the webcam feed + face mesh hidden during calibration; WebGazer still
    // processes frames, it just isn't displayed. (The ?gazeDebug preview path
    // re-enables it explicitly while answering.)
    this.showPreview(false);

    // Train ONLY via recordScreenPosition on the calibration timeline; an idle
    // cursor's move events would otherwise drag every prediction toward it.
    webgazer.removeMouseEventListeners();
    if (typeof webgazer.clearData === 'function') webgazer.clearData();

    this.running = true;
    return this;
  }

  /** Replace the external per-frame gaze callback (used by the recording layer). */
  setGazeCallback(cb) {
    this.onGaze = cb;
  }

  /**
   * Show/hide a live troubleshooting dot at the raw predicted gaze position
   * (viewport px — exactly what gets recorded). Opt-in; used during answering to
   * verify capture alignment against where the respondent is actually looking.
   */
  setDebugDot(show) {
    if (show && !this._debugDot) {
      const d = document.createElement('div');
      Object.assign(d.style, {
        position: 'fixed',
        width: '22px',
        height: '22px',
        borderRadius: '50%',
        background: 'rgba(239, 68, 68, 0.65)',
        border: '2px solid #fff',
        transform: 'translate(-50%, -50%)',
        boxShadow: '0 0 0 5px rgba(239, 68, 68, 0.25)',
        zIndex: '2147483001',
        pointerEvents: 'none',
        left: '-100px',
        top: '-100px',
      });
      document.body.appendChild(d);
      this._debugDot = d;
    } else if (!show && this._debugDot) {
      if (this._debugDot.parentNode) this._debugDot.parentNode.removeChild(this._debugDot);
      this._debugDot = null;
    }
  }

  /** Show/hide WebGazer's camera preview + face overlay (on for calibration, off while answering). */
  showPreview(show) {
    if (!this.wg) return;
    try {
      this.wg.params.showVideo = show;
      this.wg.showVideoPreview(show).showFaceOverlay(show).showFaceFeedbackBox(show);
    } catch (e) {
      /* older builds may lack a method; ignore */
    }
  }

  /** Wipe the trained model so a re-calibration starts clean. */
  clear() {
    if (this.wg && typeof this.wg.clearData === 'function') this.wg.clearData();
  }

  _makeDot(container) {
    const dot = document.createElement('div');
    Object.assign(dot.style, {
      position: 'fixed',
      width: '36px',
      height: '36px',
      background: '#2563eb',
      border: '3px solid #fff',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: '2147483000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 3px 12px rgba(0,0,0,0.4)',
      pointerEvents: 'none',
    });
    const label = document.createElement('span');
    label.style.cssText = 'font-size:13px;font-weight:bold;color:#fff;';
    dot.appendChild(label);
    container.appendChild(dot);
    return { dot, label };
  }

  /**
   * Run the point-based, non-click calibration. Renders one moving dot into
   * `container` (a fixed, full-viewport element). Resolves with capture stats.
   * @param {HTMLElement} container
   * @param {(s:{lap:number,laps:number,step:number,steps:number})=>void} [onStatus]
   */
  async calibrate(container, onStatus = null) {
    if (!this.running) throw new Error('WebGazer not started');
    const { dot, label } = this._makeDot(container);
    const steps = CALIBRATION_ORDER.length;
    let total = 0;

    try {
      for (let lap = 0; lap < LAPS; lap++) {
        for (let step = 0; step < steps; step++) {
          const pos = CALIBRATION_POSITIONS[CALIBRATION_ORDER[step]];
          dot.style.left = pos.x + '%';
          dot.style.top = pos.y + '%';

          const rect = dot.getBoundingClientRect();
          const targetX = rect.left + rect.width / 2;
          const targetY = rect.top + rect.height / 2;

          if (onStatus) onStatus({ lap: lap + 1, laps: LAPS, step: step + 1, steps });

          const reg = this.wg.getRegression()[0];
          const before = reg && reg.screenXClicksArray ? reg.screenXClicksArray.length : 0;

          const start = performance.now();
          let taken = 0;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => {
            const frame = (now) => {
              const elapsed = now - start;
              if (elapsed < SAMPLE_START_MS) {
                dot.style.background = '#4682b4';
                label.textContent = '•';
              } else {
                const sp = Math.min((elapsed - SAMPLE_START_MS) / (DWELL_MS - SAMPLE_START_MS), 1);
                dot.style.background = `hsl(${Math.round(sp * 120)}, 70%, 50%)`;
                label.textContent = Math.max(0, Math.ceil((DWELL_MS - elapsed) / 1000));
                const span = SAMPLE_END_MS - SAMPLE_START_MS;
                const dueBy = SAMPLE_START_MS + (span * taken) / (SAMPLES_PER_POINT - 1);
                if (taken < SAMPLES_PER_POINT && elapsed >= dueBy) {
                  this.wg.recordScreenPosition(targetX, targetY, 'click');
                  taken += 1;
                }
              }
              if (elapsed < DWELL_MS) requestAnimationFrame(frame);
              else resolve();
            };
            requestAnimationFrame(frame);
          });

          const after = reg && reg.screenXClicksArray ? reg.screenXClicksArray.length : 0;
          const stored = after - before;
          total += stored;
          dot.style.background = stored >= SAMPLES_PER_POINT ? '#15803d' : '#f59e0b';
          label.textContent = stored >= SAMPLES_PER_POINT ? '✓' : String(stored);
          // eslint-disable-next-line no-await-in-loop
          await sleep(200);
        }
      }
    } finally {
      if (dot.parentNode) dot.parentNode.removeChild(dot);
    }

    const expected = LAPS * steps * SAMPLES_PER_POINT;
    return { stored: total, expected, ratio: expected ? total / expected : 0 };
  }

  /**
   * Run the accuracy test: TEST_NUM_POINTS random dots, fixate each, measure the
   * mean distance from predicted gaze to target. Resolves with error stats (px).
   * @param {HTMLElement} container
   * @param {(s:{point:number,points:number})=>void} [onStatus]
   */
  async test(container, onStatus = null) {
    if (!this.running) throw new Error('WebGazer not started');

    const marginX = window.innerWidth * 0.1;
    const marginY = window.innerHeight * 0.1;
    const targets = Array.from({ length: TEST_NUM_POINTS }, () => ({
      x: marginX + Math.random() * (window.innerWidth - 2 * marginX),
      y: marginY + Math.random() * (window.innerHeight - 2 * marginY),
    }));

    const testPoint = document.createElement('div');
    Object.assign(testPoint.style, {
      position: 'fixed',
      width: '24px',
      height: '24px',
      background: 'orange',
      border: '3px solid #b30000',
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: '2147483000',
      boxShadow: '0 0 0 6px rgba(255,165,0,0.25)',
      pointerEvents: 'none',
    });
    container.appendChild(testPoint);

    const perPointErrors = [];
    let skipped = 0;
    let totalSamples = 0;

    try {
      for (let i = 0; i < targets.length; i++) {
        const t = targets[i];
        testPoint.style.left = t.x + 'px';
        testPoint.style.top = t.y + 'px';
        if (onStatus) onStatus({ point: i + 1, points: targets.length });

        this._collecting = false;
        this._buffer = [];
        // eslint-disable-next-line no-await-in-loop
        await sleep(TEST_SETTLE_MS);

        this._collecting = true;
        // eslint-disable-next-line no-await-in-loop
        await sleep(TEST_COLLECT_MS);
        this._collecting = false;

        const buf = this._buffer;
        if (buf.length === 0) {
          skipped += 1;
          continue;
        }
        totalSamples += buf.length;
        const meanX = buf.reduce((s, p) => s + p.x, 0) / buf.length;
        const meanY = buf.reduce((s, p) => s + p.y, 0) / buf.length;
        perPointErrors.push(Math.hypot(meanX - t.x, meanY - t.y));
      }
    } finally {
      this._collecting = false;
      this._buffer = [];
      if (testPoint.parentNode) testPoint.parentNode.removeChild(testPoint);
    }

    if (perPointErrors.length === 0) {
      return { meanError: null, worst: null, skipped, points: 0, samples: 0 };
    }
    const meanError = perPointErrors.reduce((a, b) => a + b, 0) / perPointErrors.length;
    return {
      meanError,
      worst: Math.max(...perPointErrors),
      skipped,
      points: perPointErrors.length,
      samples: totalSamples,
    };
  }

  /** Tear WebGazer down and release the camera. */
  stop() {
    if (this.wg && this.running) {
      // WebGazer's end() only pauses the loop and removes its DOM nodes — it does
      // NOT stop the camera track, so the browser keeps showing "camera in use".
      // Capture the live stream from the video feed first, then stop its tracks
      // after end() to actually release the camera.
      let stream = null;
      try {
        const feed = document.getElementById('webgazerVideoFeed');
        if (feed && feed.srcObject) stream = feed.srcObject;
      } catch (e) {
        /* no-op */
      }
      try {
        this.wg.end();
      } catch (e) {
        /* no-op */
      }
      try {
        if (stream && stream.getTracks) {
          stream.getTracks().forEach((t) => t.stop());
        }
      } catch (e) {
        /* no-op */
      }
    }
    this.setDebugDot(false);
    this.running = false;
    this.onGaze = null;
    this._collecting = false;
    this._buffer = [];
  }
}

export const ACCEPTABLE_ERROR_PX = 200;
export const MAX_CALIBRATION_ATTEMPTS = 3;

export default WebgazerController;
